import { api, unwrap } from '@saas-kit/lib';

import type {
  AdminInvoice,
  AdminInvoiceDetail,
  AdminInvoiceList,
  AdminPayment,
  AdminSubscription,
  CreateInvoicePayload,
  IssueInvoicePayload,
  ListInvoicesParams,
  MarkPaidPayload,
  RecordPaymentPayload,
  VoidInvoicePayload,
} from './types';

export * from './types';

export async function listInvoices(
  tenantId: string,
  params: ListInvoicesParams = {},
): Promise<AdminInvoiceList> {
  return unwrap<AdminInvoiceList>(
    api.get(`/admin/billing/invoices/${tenantId}`, {
      params: {
        offset: params.offset ?? 0,
        limit: params.limit ?? 25,
        ...(params.status ? { status: params.status } : {}),
      },
    }),
  );
}

export async function getInvoice(
  tenantId: string,
  invoiceId: string,
): Promise<AdminInvoiceDetail> {
  return unwrap<AdminInvoiceDetail>(
    api.get(`/admin/billing/invoices/${tenantId}/${invoiceId}`),
  );
}

export async function createInvoice(
  tenantId: string,
  payload: CreateInvoicePayload,
): Promise<AdminInvoice> {
  return unwrap<AdminInvoice>(
    api.post(`/admin/billing/invoices/${tenantId}`, payload),
  );
}

export async function issueInvoice(
  tenantId: string,
  invoiceId: string,
  payload: IssueInvoicePayload = {},
): Promise<AdminInvoice> {
  return unwrap<AdminInvoice>(
    api.post(`/admin/billing/invoices/${tenantId}/${invoiceId}/issue`, payload),
  );
}

export async function markInvoicePaid(
  tenantId: string,
  invoiceId: string,
  payload: MarkPaidPayload = {},
): Promise<AdminInvoice> {
  return unwrap<AdminInvoice>(
    api.post(
      `/admin/billing/invoices/${tenantId}/${invoiceId}/mark-paid`,
      payload,
    ),
  );
}

export async function voidInvoice(
  tenantId: string,
  invoiceId: string,
  payload: VoidInvoicePayload = {},
): Promise<AdminInvoice> {
  return unwrap<AdminInvoice>(
    api.post(`/admin/billing/invoices/${tenantId}/${invoiceId}/void`, payload),
  );
}

export async function recordPayment(
  tenantId: string,
  invoiceId: string,
  payload: RecordPaymentPayload,
): Promise<AdminPayment> {
  return unwrap<AdminPayment>(
    api.post(
      `/admin/billing/invoices/${tenantId}/${invoiceId}/payments`,
      payload,
    ),
  );
}

export async function voidPayment(
  tenantId: string,
  paymentId: string,
): Promise<AdminPayment> {
  return unwrap<AdminPayment>(
    api.post(`/admin/billing/payments/${tenantId}/${paymentId}/void`, {}),
  );
}

export async function getSubscription(
  tenantId: string,
): Promise<AdminSubscription | null> {
  try {
    return await unwrap<AdminSubscription>(
      api.get(`/admin/billing/subscriptions/${tenantId}`),
    );
  } catch (err) {
    if (
      err &&
      typeof err === 'object' &&
      'response' in err &&
      (err as { response?: { status?: number } }).response?.status === 404
    ) {
      return null;
    }
    throw err;
  }
}
